import React from "react";
import _ from "lodash";
import { notify } from "react-notify-toast";
import { GamAccountAPI } from "apis";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import Checkbox from "components/common/Checkbox";
import ModalWrapper from "components/common/ModalWrapper";
import HighLightRowWrapper from "components/common/HighLightRowWrapper";
import NetworkTextDisplay from "components/common/NetworkTextDisplay";
import {
  thClass,
  tdClass,
  inputClass,
  buttonConfirmClass,
  buttonModalConfirmClass,
  buttonModalCancelClass,
  buttonActionClassGray,
  buttonActionDisableGray,
} from "helpers/StyleClass.js";
import { MESSAGE } from "constants/Message";
import PromptModal from "components/common/PromptModal";

import MessageText from "components/common/MessageText";

const PROBLEM = {
  NORMAL: "NORMAL",
  UNAUTHORIZED: "UNAUTHORIZED",
};

class OnboardAccount extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      createMsg: [],
      isOpenViewModal: false,

      accountData: [],
      intervalId: null,
      timestamp: null,
      name: "",
      authCode: null,
      authAccount: null,

      selected: this.props.selectedAccount
        ? this.props.selectedAccount.accountId
        : null,

      isLoading: false,
      isPromptModalOpen: false,
      promptHeader: null,
      promptMessage: null,
      promptAction: null,
      promptMsg: [],
    };

    this.handleConfirm = this.handleConfirm.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleCheckChange = this.handleCheckChange.bind(this);
    this.handleAccessOAuth = this.handleAccessOAuth.bind(this);
    this.handleGetAuthMessage = this.handleGetAuthMessage.bind(this);
    this.handleAuthorize = this.handleAuthorize.bind(this);
    this.handleSync = this.handleSync.bind(this);
    this.updateData = this.updateData.bind(this);
  }

  async componentDidMount() {
    const { pubId, handleLoading } = this.props;
    const { selected } = this.state;

    handleLoading(true);

    const result = await GamAccountAPI.getGamAccounts({ pubId });
    if (result) {
      this.setState({ accountData: result }, () => {
        if (result.length === 1 && !selected) {
          this.handleCheckChange(result[0].accountId);
        }
      });
    }

    handleLoading(false);
  }

  componentWillUnmount() {
    const { intervalId } = this.state;
    if (intervalId) {
      clearInterval(intervalId);
    }
    window.removeEventListener("message", this.handleGetAuthMessage);
  }

  handleCheckChange(accountId) {
    const { selected, accountData } = this.state;
    const changed = selected === accountId ? null : accountId;
    this.setState({
      selected: changed,
    });

    const account = accountData.find((item) => item.accountId === changed);
    this.props.handleChange(account);
  }

  // Access Google OAuth2.0
  handleAccessOAuth() {
    try {
      this.props.handleLoading(true);
      window.removeEventListener("message", this.handleGetAuthMessage);

      const timestamp = Date.now();
      const scopes =
        "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/dfp";
      const access = {
        url: "https://accounts.google.com/o/oauth2/v2/auth",
        params: {
          scope: encodeURIComponent(scopes),
          include_granted_scopes: true,
          redirect_uri: this._getRedirectUri(),
          response_type: "code",
          client_id: process.env.REACT_APP_GOOGLE_SERVICE_CLIENT_ID,
          prompt: "consent",
          access_type: "offline",
          state: encodeURIComponent(JSON.stringify({ timestamp })),
        },
      };

      const accessUrl = `${access.url}?${Object.entries(access.params)
        .map(([key, value]) => `${key}=${value}`)
        .join("&")}`;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        accessUrl,
        "Google OAuth2.0",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      this.setState({ timestamp });

      window.addEventListener("message", this.handleGetAuthMessage);

      const checkWindow = setInterval(() => {
        try {
          if (popup && popup.closed) {
            console.log("Popup has been closed");
            clearInterval(checkWindow);
            this.props.handleLoading(false);
            this.setState({ intervalId: null });
            window.removeEventListener("message", this.handleGetAuthMessage);
          }
        } catch (error) {
          console.log("Popup has been closed");
        }
      }, 1000);

      this.setState({ intervalId: checkWindow });
    } catch (error) {
      notify.show("Could not open Google OAuth", "error");
      console.error(error);
    }
  }

  // Get Google OAuth2.0 AuthCode
  handleGetAuthMessage(event) {
    if (event.origin !== window.location.origin) return;

    const {
      data: { code, state, error },
    } = event;

    if (error) {
      notify.show(error, MESSAGE.ERROR);
      return;
    }

    if (!code || !state) return;

    const { timestamp } = JSON.parse(decodeURIComponent(state));
    if (timestamp !== this.state.timestamp) return;

    window.removeEventListener("message", this.handleGetAuthMessage);
    this.props.handleLoading(false);
    this.setState({ authCode: code }, () => {
      if (this.state.authAccount) {
        this.handleAuthorize(this.state.authAccount);
      } else {
        this.setState({ isOpenViewModal: true, createMsg: null });
      }
    });
  }

  // Confirm for Add GAM Account
  async handleConfirm() {
    const { pubId } = this.props;
    const { authCode, name } = this.state;

    this.setState({ createMsg: [] });

    if (!authCode) {
      this.setState({
        createMsg: ["authCode is missing.", MESSAGE.ERROR],
      });
      return;
    }

    if (name === "") {
      this.setState({
        createMsg: ["Name is required.", MESSAGE.ERROR],
      });
      return;
    }

    this.props.handleLoading(true);
    try {
      const result = await GamAccountAPI.createGamAccount({
        pubId,
        name,
        authorizationCode: authCode,
        redirectUri: this._getRedirectUri(),
      });

      if (result) {
        this.updateData(result);
        this.setState({ createMsg: ["Add Success", MESSAGE.SUCCESS] });
      }
    } catch (error) {
      this.setState({ createMsg: [error, MESSAGE.ERROR] });
    }

    this.props.handleLoading(false);
  }

  // Action: Authorize GAM Account
  async handleAuthorize({ accountId, name }, isPrompt = true) {
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptMsg: [],
        promptHeader: "Authorize GAM Account",
        promptMessage: (
          <>
            Are you sure you want to authorize this GAM account? <br />
            <span className="font-semibold text-blue-600">
              {accountId} {name}
            </span>
          </>
        ),
        promptAction: () => this.handleAuthorize({ accountId }, false),
      });
      return;
    }

    this.setState({ isLoading: true, promptMsg: [] });
    try {
      const result = await GamAccountAPI.authorizeGamAccount({
        accountId,
        authorizationCode: this.state.authCode,
        redirectUri: this._getRedirectUri(),
      });

      if (result) {
        this.updateData(result);
        this.setState({ promptMsg: ["Authorize Success", MESSAGE.SUCCESS] });
      }
    } catch (error) {
      this.setState({ promptMsg: [error, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  // Action: Sync GAM Account
  async handleSync({ accountId, name }, isPrompt = true) {
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptMsg: [],
        promptHeader: "Sync GAM Account",
        promptMessage: (
          <>
            Are you sure you want to sync this GAM account? <br />
            <span className="font-semibold text-blue-600">
              {accountId} {name}
            </span>
          </>
        ),
        promptAction: () => this.handleSync({ accountId }, false),
      });
      return;
    }

    this.setState({ isLoading: true, promptMsg: [] });
    try {
      const result = await GamAccountAPI.syncGamAccount({ accountId });

      if (result) {
        this.updateData(result);
        this.setState({ promptMsg: ["Sync Success", MESSAGE.SUCCESS] });
      }
    } catch (error) {
      this.setState({ promptMsg: [error, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  handleClose() {
    this.setState({
      isOpenViewModal: false,
      authCode: null,
      name: "",
      createMsg: [],
    });
  }

  updateData(account) {
    const data = _.cloneDeep(this.state.accountData);
    const index = data.findIndex(
      (item) => item.accountId === account.accountId,
    );
    if (index < 0) {
      data.push(account);
    } else {
      data[index] = account;
    }
    this.setState({ accountData: data }, () => {
      const { selected } = this.state;
      if (selected !== account.accountId) {
        this.handleCheckChange(account.accountId);
      } else {
        this.props.handleChange(account);
      }
    });
  }

  _getRedirectUri() {
    return encodeURIComponent(`${window.location.origin}/oauth-callback.html`);
  }

  render() {
    const {
      accountData,
      selected,
      isOpenViewModal,
      createMsg,
      isLoading,
      isPromptModalOpen,
      promptAction,
      promptHeader,
      promptMessage,
      promptMsg,
    } = this.state;

    return (
      <div>
        <div className="mb-2 flex justify-end">
          <button
            className={buttonConfirmClass}
            onClick={() => {
              this.setState({ authAccount: null });
              this.handleAccessOAuth();
            }}
          >
            Add Account
          </button>
        </div>

        <table className="shadow divide-y min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className={`${thClass}`} style={{ width: "2rem" }}></th>
              <th className={`${thClass} w-20`}>ID</th>
              <th className={`${thClass} w-64`}>Name</th>
              <th className={`${thClass} w-40`}>Status</th>
              <th className={thClass}>Networks</th>
              <th className={`${thClass} w-56`}>Created At</th>
              <th className={`${thClass} w-48 text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y bg-white text-sm">
            {accountData.map((item, idx) => (
              <HighLightRowWrapper
                selected={selected === item.accountId}
                key={`${item.accountId}-${idx}`}
                onClick={() => {
                  this.handleCheckChange(item.accountId);
                }}
              >
                <td className={tdClass}>
                  <Checkbox
                    isChecked={selected === item.accountId}
                    onChange={() => {
                      this.handleCheckChange(item.accountId);
                    }}
                  ></Checkbox>
                </td>
                <td className={tdClass}>{item.accountId}</td>
                <td className={tdClass}>{item.name}</td>
                <td className={tdClass}>
                  {item.status}
                  <div
                    className={`${
                      item.problems.indexOf(PROBLEM.UNAUTHORIZED) !== -1
                        ? "font-semibold text-red-400"
                        : ""
                    }`}
                  >
                    {item.problems.indexOf(PROBLEM.UNAUTHORIZED) !== -1
                      ? _.startCase(_.toLower(PROBLEM.UNAUTHORIZED))
                      : ""}
                  </div>
                </td>
                <td className={tdClass}>
                  {item.gamNetworks.length === 0
                    ? "--"
                    : item.gamNetworks.map((network) => (
                        <NetworkTextDisplay
                          network={network}
                          key={network.networkId}
                        ></NetworkTextDisplay>
                      ))}
                </td>
                <td className={tdClass}>
                  <DateTimeFormatter
                    datetime={item.createdAt}
                    isLineBreak={true}
                  ></DateTimeFormatter>
                </td>
                <td className={tdClass}>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className={
                        item.problems.indexOf(PROBLEM.UNAUTHORIZED) !== -1
                          ? buttonActionClassGray
                          : buttonActionDisableGray
                      }
                      onClick={(e) => {
                        this.setState({ authAccount: item });
                        this.handleAccessOAuth();
                      }}
                      disabled={
                        item.problems.indexOf(PROBLEM.UNAUTHORIZED) === -1
                      }
                    >
                      Authorize
                    </button>
                    <button
                      className={buttonActionClassGray}
                      onClick={(e) => {
                        this.handleSync(item);
                      }}
                    >
                      Sync
                    </button>
                  </div>
                </td>
              </HighLightRowWrapper>
            ))}
          </tbody>
        </table>

        <ModalWrapper
          isOpen={isOpenViewModal}
          showCloseFooter={false}
          width="30%"
          title="New GAM Account"
        >
          <div>
            <label>Name</label>
            <input
              type="text"
              className={inputClass}
              placeholder="example@domain.com..."
              onChange={(e) => this.setState({ name: e.target.value })}
            ></input>

            <div className="mt-2">
              <MessageText msg={createMsg}></MessageText>
            </div>
            <div className="mt-2 flex items-center justify-end gap-2">
              {createMsg && createMsg[1] === MESSAGE.SUCCESS ? (
                <>
                  <button
                    type="button"
                    className={buttonModalConfirmClass}
                    onClick={this.handleClose}
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={buttonModalCancelClass}
                    onClick={this.handleClose}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className={buttonModalConfirmClass}
                    onClick={this.handleConfirm}
                  >
                    Proceed
                  </button>
                </>
              )}
            </div>
          </div>
        </ModalWrapper>

        {isPromptModalOpen && (
          <PromptModal
            isLoading={isLoading}
            isOpenModal={isPromptModalOpen}
            handleConfirm={promptAction}
            msg={promptMsg}
            handleCancel={() =>
              this.setState({ isPromptModalOpen: false, promptMsg: [] })
            }
            header={promptHeader}
          >
            {promptMessage}
          </PromptModal>
        )}
      </div>
    );
  }
}
export default OnboardAccount;
